import { useEffect, useState } from 'react';

const STORAGE_KEY = 'mc:muted';

/**
 * Tiny mute toggle in the header. Persisted across reloads in localStorage.
 *
 * It dispatches a custom event `mc:muted-changed` that the `useAudio` hook
 * listens to — that way we don't need a global state library just for this.
 */
export default function MuteButton() {
  const [muted, setMuted] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem(STORAGE_KEY) === '1';
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, muted ? '1' : '0');
    window.dispatchEvent(new CustomEvent('mc:muted-changed', { detail: muted }));
  }, [muted]);

  return (
    <button
      type="button"
      onClick={() => setMuted((m) => !m)}
      aria-label={muted ? 'Unmute' : 'Mute'}
      title={muted ? 'Unmute' : 'Mute'}
      className="ml-2 inline-flex h-7 w-7 items-center justify-center
                 rounded-full border border-gold-500/30 bg-night-800/80
                 text-gold-300 backdrop-blur transition
                 hover:text-gold-200 active:scale-95"
    >
      {muted ? (
        // Speaker with slash
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
          <path d="M5 9v6h3l5 4V5L8 9H5z" fill="currentColor" />
          <line x1="3" y1="3" x2="21" y2="21" stroke="currentColor" strokeWidth="2" />
        </svg>
      ) : (
        // Speaker with waves
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
          <path d="M5 9v6h3l5 4V5L8 9H5z" fill="currentColor" />
          <path d="M17 8a5 5 0 0 1 0 8" stroke="currentColor" strokeWidth="1.5" fill="none" />
        </svg>
      )}
    </button>
  );
}


/** Helper for non-React code (e.g. the useAudio hook). */
export function isMuted(): boolean {
  if (typeof window === 'undefined') return false;
  return localStorage.getItem(STORAGE_KEY) === '1';
}
